import { Link, NavLink, Outlet } from "react-router-dom";
import { ShoppingCart, Menu, Mail, Phone, Instagram, Facebook, MessageCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCart } from "@/hooks/useCart";
import { useSitePages } from "@/hooks/useSitePage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AccessibilityMenu } from "@/components/a11y/AccessibilityMenu";
import { CookieBanner } from "@/components/CookieBanner";
import { AlcoholWarning } from "@/components/AlcoholWarning";
import { AgeGate } from "@/components/AgeGate";
import { SiteLogo } from "@/components/layout/SiteLogo";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export function SiteLayout() {
  const { totalQty } = useCart();
  const [open, setOpen] = useState(false);
  // Footer + header structure is fully sourced from the CMS so the
  // proprietor can edit nav links, columns, and visibility flags without
  // touching code. site.headerConfig / footerConfig drive the layout;
  // contact (phone/email/social) still lives in its own key.
  const { data: site } = useQuery({
    queryKey: ["site-content"],
    queryFn: api.getSiteContent,
    staleTime: 5_000,
  });
  const { data: pages } = useSitePages();
  const contact = site?.contact;
  const headerCfg = site?.headerConfig;
  const footerCfg = site?.footerConfig;

  const nav = useMemo(() => {
    const cmsConfigured =
      headerCfg?.navLinks?.filter((n) => n.visible).map((n) => ({ to: n.url, label: n.label })) ?? [];
    const cmsConfiguredSet = new Set(cmsConfigured.map((n) => n.to));
    const dynamicPages = (pages?.pages ?? [])
      .filter((p) => p.inNav)
      .map((p) => ({ to: `/${p.slug}`, label: p.title }))
      .filter((n) => !cmsConfiguredSet.has(n.to));
    return [...cmsConfigured, ...dynamicPages];
  }, [headerCfg, pages]);
  const telHref = contact?.phone ? `tel:${contact.phone.replace(/[^0-9+]/g, "")}` : null;
  const mailHref = contact?.email ? `mailto:${contact.email}` : null;
  const waHref = contact?.whatsappPhone
    ? `https://wa.me/${contact.whatsappPhone.replace(/[^0-9]/g, "")}`
    : null;
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <a href="#main" className="skip-link">דלג לתוכן הראשי</a>

      {/* ─── HEADER ────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="container flex items-center justify-between h-20 gap-4">
          {(headerCfg?.showLogo ?? true) && (
            <Link to="/" className="flex items-center gap-2 group" aria-label="כרם נבות — דף הבית">
              <SiteLogo variant="header" />
            </Link>
          )}

          <nav className="hidden md:flex items-center gap-8" aria-label="ראשי">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "text-sm font-medium tracking-wide transition-colors relative after:absolute after:-bottom-1 after:right-0 after:h-px after:bg-primary after:transition-all",
                    isActive
                      ? "text-primary after:w-full"
                      : "text-foreground/80 hover:text-primary after:w-0 hover:after:w-full",
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1">
            {(headerCfg?.showAccessibilityMenu ?? true) && <AccessibilityMenu />}
            {(headerCfg?.showCart ?? true) && (
              <Button asChild variant="ghost" size="icon" aria-label={`עגלה — ${totalQty} פריטים`}>
                <Link to="/cart" className="relative">
                  <ShoppingCart className="h-5 w-5" />
                  {totalQty > 0 && (
                    <span
                      className="absolute -top-1 -left-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold"
                      aria-hidden
                    >
                      {totalQty}
                    </span>
                  )}
                </Link>
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-label="תפריט"
              onClick={() => setOpen((v) => !v)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
        {open && (
          <nav id="mobile-nav" className="md:hidden border-t border-border/60 bg-background" aria-label="ראשי נייד">
            <div className="container py-3 flex flex-col">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn("py-2 text-sm font-medium hover:text-primary", isActive && "text-primary")
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      {/* ─── FOOTER ────────────────────────────────────────────────────── */}
      <footer
        className="mt-12 text-primary-foreground"
        style={{ backgroundColor: "hsl(var(--surface-dark))" }}
      >
        <div
          className={cn(
            "container py-16 grid gap-10",
            (footerCfg?.columns?.length ?? 2) >= 2 ? "md:grid-cols-4" : "md:grid-cols-3",
          )}
        >
          <div className={cn((footerCfg?.columns?.length ?? 2) >= 2 ? "md:col-span-2" : "md:col-span-2")}>
            {(footerCfg?.showLogo ?? true) && (
              <div className="flex items-center gap-3 mb-4">
                <SiteLogo variant="footer" />
              </div>
            )}
            {footerCfg?.brandText && (
              <p className="text-primary-foreground/75 leading-relaxed max-w-md whitespace-pre-line">
                {footerCfg.brandText}
              </p>
            )}
            {(footerCfg?.showAddress ?? true) &&
              (contact?.addressLine1 || contact?.addressLine2 || contact?.hoursLine) && (
                <div className="mt-5 text-sm text-primary-foreground/75 leading-relaxed">
                  {contact?.addressLine1 && <div>{contact.addressLine1}</div>}
                  {contact?.addressLine2 && <div>{contact.addressLine2}</div>}
                  {contact?.hoursLine && <div className="mt-1">{contact.hoursLine}</div>}
                </div>
              )}
            {(footerCfg?.showSocialIcons ?? true) && (
              <div className="flex items-center gap-4 mt-6 text-primary-foreground/80">
                {mailHref && (
                  <a href={mailHref} aria-label="אימייל" className="hover:text-accent transition-colors">
                    <Mail className="h-5 w-5" />
                  </a>
                )}
                {telHref && (
                  <a href={telHref} aria-label="טלפון" className="hover:text-accent transition-colors">
                    <Phone className="h-5 w-5" />
                  </a>
                )}
                {waHref && (
                  <a
                    href={waHref}
                    rel="noreferrer noopener nofollow"
                    target="_blank"
                    aria-label="ווטסאפ"
                    className="hover:text-accent transition-colors"
                  >
                    <MessageCircle className="h-5 w-5" />
                  </a>
                )}
                {contact?.instagramUrl && (
                  <a
                    href={contact.instagramUrl}
                    rel="noreferrer noopener nofollow"
                    target="_blank"
                    aria-label="אינסטגרם"
                    className="hover:text-accent transition-colors"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
                {contact?.facebookUrl && (
                  <a
                    href={contact.facebookUrl}
                    rel="noreferrer noopener nofollow"
                    target="_blank"
                    aria-label="פייסבוק"
                    className="hover:text-accent transition-colors"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {(footerCfg?.columns ?? [])
            .filter((c) => c.visible)
            .map((col, ci) => (
              <div key={ci}>
                {col.title && (
                  <div className="font-serif text-lg font-semibold mb-4 text-primary-foreground">
                    {col.title}
                  </div>
                )}
                <ul className="space-y-2 text-sm text-primary-foreground/75">
                  {col.links
                    .filter((l) => l.visible)
                    .map((link, li) => (
                      <li key={li}>
                        <FooterLink to={link.url}>{link.label}</FooterLink>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
        </div>
        <div className="container pb-10">
          <FooterNewsletter />
        </div>
        {/* Statutory alcohol warning. Visual + text mandated by law and
            verified by server/health-checks/suites/regulation.ts. */}
        {(footerCfg?.showAlcoholWarning ?? true) && (
          <div className="bg-white border-t border-primary-foreground/15">
            <div className="container py-5">
              <AlcoholWarning />
            </div>
          </div>
        )}
        {((footerCfg?.showCopyright ?? true) || (footerCfg?.showAgeStatement ?? true)) && (
          <div className="border-t border-primary-foreground/15">
            <div className="container py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-primary-foreground/60">
              {(footerCfg?.showCopyright ?? true) && (
                <div>
                  © {new Date().getFullYear()}{" "}
                  {footerCfg?.copyrightText?.replace(/^©\s*/, "").replace(/^\d{4}\s*/, "") ??
                    "כרם נבות. כל הזכויות שמורות."}
                </div>
              )}
              {(footerCfg?.showAgeStatement ?? true) && (
                <div>{footerCfg?.ageStatementText ?? "החל מגיל 18 בלבד."}</div>
              )}
            </div>
          </div>
        )}
      </footer>

      <CookieBanner />
      <AgeGate />
    </div>
  );
}

/* ─── footer link (internal vs external/anchor) ──────────────────────── */

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  // External links (http(s)://) and tel/mailto open via <a>; everything
  // else routes through React Router's <Link> so SPA navigation works.
  const isExternal = /^(https?:|mailto:|tel:|wa:)/i.test(to);
  const isAnchor = to.startsWith("#") || to.includes("#") && to.startsWith("/");
  if (isExternal) {
    return (
      <a
        href={to}
        rel="noreferrer noopener nofollow"
        target={to.startsWith("http") ? "_blank" : undefined}
        className="hover:text-accent"
      >
        {children}
      </a>
    );
  }
  if (isAnchor) {
    return (
      <a href={to} className="hover:text-accent">
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className="hover:text-accent">
      {children}
    </Link>
  );
}

/* ─── footer newsletter signup ───────────────────────────────────────── */

/**
 * Footer newsletter signup — copy comes from the CMS `footerNewsletter`
 * global key, edited in /admin/content → Global. The image (if any) is
 * stored at the slot "newsletter-footer", same convention as the page
 * block uses for per-instance images.
 *
 * Hidden when `visible` is false in the CMS so the admin can drop the
 * footer signup entirely without code changes.
 */
function FooterNewsletter() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const { data: site } = useQuery({
    queryKey: ["site-content"],
    queryFn: api.getSiteContent,
    staleTime: 5_000,
  });
  const nl = site?.footerNewsletter;
  const image = site?.images?.["newsletter-footer"] ?? null;

  const m = useMutation({
    mutationFn: (e: string) => api.subscribeNewsletter({ email: e, source: "footer" }),
    onSuccess: () => {
      setDone(true);
      setEmail("");
    },
    onError: () => toast.error("ההרשמה נכשלה. אפשר לנסות שוב."),
  });

  if (!nl || nl.visible === false) return null;

  if (done) {
    return (
      <div className="rounded-xl border border-primary-foreground/15 bg-primary-foreground/5 p-5 text-center text-primary-foreground/85 max-w-2xl mx-auto">
        {nl.successMessage}
      </div>
    );
  }

  const showImage = nl.imageMode !== "none" && !!image;
  const FormCard = (
    <form
      className="rounded-xl border border-primary-foreground/15 bg-primary-foreground/5 p-5 max-w-2xl mx-auto w-full"
      onSubmit={(e) => {
        e.preventDefault();
        if (!email.trim()) return;
        m.mutate(email.trim());
      }}
    >
      {nl.eyebrow && (
        <div className="text-xs uppercase tracking-[0.2em] text-primary-foreground/60 mb-1">
          {nl.eyebrow}
        </div>
      )}
      {nl.title && (
        <div className="font-serif text-lg font-semibold mb-1 text-primary-foreground">
          {nl.title}
        </div>
      )}
      {nl.subtitle && (
        <p className="text-sm text-primary-foreground/75 mb-3 leading-relaxed whitespace-pre-line">
          {nl.subtitle}
        </p>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          type="email"
          required
          dir="ltr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          className="bg-primary-foreground text-foreground placeholder:text-foreground/50"
          aria-label="כתובת אימייל"
        />
        <Button
          type="submit"
          disabled={m.isPending}
          className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
        >
          {m.isPending ? "שולח…" : nl.buttonLabel}
        </Button>
      </div>
    </form>
  );

  if (nl.imageMode === "above" && showImage) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <img src={image} alt="" className="w-full h-auto block rounded-xl" />
        {FormCard}
      </div>
    );
  }
  if (nl.imageMode === "side" && showImage) {
    const onLeft = nl.imageAlign === "left" || nl.imageAlign === "start";
    const size = nl.imageSize ?? "matchWidth";
    const onLeftCls = onLeft ? "md:order-2" : "md:order-1";
    const formCellCls = onLeft ? "md:order-1" : "md:order-2";

    // matchHeight: form drives the row height, image is absolutely
    // positioned inside its cell so it scales to fit without inflating
    // the row to its own intrinsic height.
    if (size === "matchHeight") {
      return (
        <div className="grid gap-4 md:gap-6 md:grid-cols-2 max-w-4xl mx-auto items-stretch">
          <div className={cn(onLeftCls, "relative min-h-0")}>
            <img
              src={image}
              alt=""
              className="absolute inset-0 w-full h-full object-contain rounded-xl"
            />
          </div>
          <div className={formCellCls}>{FormCard}</div>
        </div>
      );
    }

    const imgClasses =
      size === "natural"
        ? "max-w-full h-auto block rounded-xl mx-auto"
        : "w-full h-auto block rounded-xl";
    return (
      <div className="grid gap-4 md:gap-6 md:grid-cols-2 max-w-4xl mx-auto items-center">
        <div className={onLeftCls}>
          <img src={image} alt="" className={imgClasses} />
        </div>
        <div className={formCellCls}>{FormCard}</div>
      </div>
    );
  }
  return FormCard;
}
